// src/annihilation/skilltrees/spectralTiger.js
// ============================================================
//  ANNIHILATION — Spectral Tiger Lineage Tree.
//  Source of truth: ANNIHILATION_SKILL_TREES (v2), Part 2.
//
//  35 nodes · 5 clusters · 3 key forks. THE TEMPLATE — the other
//  three lineages (Chaos Dragon, Z4Z, Xulan) copy this file's
//  SHAPE, not new architecture (Batches 12–14, doc-then-code,
//  one being at a time).
//
//  Node-count note (matches the doc exactly): the tables list 38
//  rows; the three fork PAIRS (L5a/b, G4a/b, E6a/b) each occupy
//  one node "slot," giving the doc's stated 35. Both fork halves
//  ship as entries here; the engine's fork lock makes only one
//  ownable.
//
//  All legality flows through treeEngine (R-UNIVERSAL). This
//  file is pure data.
//
//  ⚑⚑ BATCH GATE-1 (caP'n, Jul 25 2026) ⚑⚑
//  `tierReq` is gone from this file entirely — four nodes carried
//  it, and all four now carry `aenergyReq` instead. The rule they
//  express is unchanged in FEEL and completely changed in KIND:
//  the tree no longer asks how old you are, it asks what you can
//  carry. Each threshold below is priced in the Aenergy the
//  ability actually leans on (Doctrine Part 3), and each one is
//  CIRCUMVENTABLE — a Tome or a Heresy that pushes your Lyt past
//  its natural allotment opens Nine Lives early, on purpose.
//  That is the whole design. THE STEERING LAW.
//
//  ⚑ These four are the only thresholds authored so far. The
//  remaining 31 nodes in this tree are ungated and stay that way
//  until the full per-node data pass (roadmap §4.38) — which is
//  sequenced AFTER the engine deliberately, so the numbers get
//  tuned with a controller in hand rather than in a document.
//
//  ⚑⚑ THE CUT (Power Doctrine · Jul 25 2026) ⚑⚑
//  38 nodes → 27. The tree was priced for a budget that does not
//  exist: 193 nodes across the game against 25 skill points at
//  Lord and 53 at level 200. Points are SCARCE AND PERMANENT —
//  an untouchable design pillar — so a menu nobody can read is
//  not generosity, it is noise.
//
//  Eleven cut, almost all by one rule:
//
//    > THE DIAL-IS-NOT-A-NODE LAW. If a node's entire effect is
//    > "the number in the base ability is now a bigger number,"
//    > it is not a node. It is a balance pass wearing a node's
//    > costume. Cut it, and fold its value into the base.
//
//   · st_l1 Keen Claws       (+5 flat on one swipe)  → folded
//   · st_l3 Silent Step      stealth with no stealth system, and
//                            gated behind another node's
//                            condition → RELOCATED to the Sense
//                            "Deep Listen" (senses.js). Cut
//                            features must relocate their
//                            signature, never evaporate.
//   · st_h1 Paws Reach       range on an auto-targeting Primera;
//                            invisible to the player
//   · st_h3 Paws Amplif.     percentage magnitude, and redundant
//                            with Spectral Claws' own
//                            missing-Anima scaling
//   · st_h5 Homing Claws     wall-piercing projectiles is a real
//                            physics cost in a 3D zone for a
//                            small gain
//   · st_h7 Haunting Echo    (5s → 10s bleed) dial
//   · st_h8 Haunting Reach   zone-wide U'hlti breaks encounter
//                            design AND the renderer's culling
//   · st_g3 Swift Spirit     (2× → 3× speed) a movement-feel
//                            problem, not a build
//   · st_k2 Fuller Grace     (50% → 75%) dial → folded to 60%
//   · st_k3 Shadowed Return  → folded into Nine Lives' base. It
//                            should always have been there.
//   · st_e3 Extended Realm   duration bump; the same node as
//                            st_e2 in a different hat
//
//  SURVIVES UNTOUCHED: every fork (l5a/b, g4a/b, e6a/b), every
//  slot-unlock (g1, k1, e1), every capstone, and st_e5 Memory
//  Chain — which was the only node in the game that understood
//  what an Emperium is, and is now the template for THE EMPERIUM
//  LAW (the world stays changed after it ends).
//
//  ⚑ A save holding a cut id gets its point RETURNED by
//  treeEngine.reconcileTreeState. A deleted node may never
//  silently eat a permanent point.
// ============================================================

import { NODE_TYPES, TREE_IDS } from "./treeEngine";

export const SPECTRAL_TIGER_CLUSTERS = {
  lyt: { id: "lyt", name: "Lyt Cluster", focus: "Phantom Strike enhancements" },
  haunting: {
    id: "haunting",
    name: "Haunting Cluster",
    focus: "Primera → Impera → U'hlti upgrade line",
  },
  ghost_form: {
    id: "ghost_form",
    name: "Ghost Form Cluster",
    focus: "Transformation investments",
  },
  grace: { id: "grace", name: "Grace Cluster", focus: "King's Grace — Nine Lives path" },
  emperium: {
    id: "emperium",
    name: "Emperium Cluster",
    focus: "The Ghost Realm — endgame ultimate",
  },
};

const P = NODE_TYPES.PASSIVE;
const A = NODE_TYPES.ACTIVE;
const F = NODE_TYPES.FORK;
const C = NODE_TYPES.CAPSTONE;

export const SPECTRAL_TIGER_TREE = {
  treeId: TREE_IDS.LINEAGE,
  beingId: "spectral_tiger",
  name: "Spectral Tiger — Lineage",
  nodeSlots: 27, // ⚑ was 35/38 — see THE CUT in the header
  clusters: SPECTRAL_TIGER_CLUSTERS,
  nodes: [
    // ---------------- LYT CLUSTER (6 slots) ----------------
    {
      id: "st_l2",
      name: "Phase Momentum",
      type: P,
      cluster: "lyt",
      effect: "After dodging an attack, the next Phantom Strike costs 0 Lyt.",
    },
    {
      id: "st_l4",
      name: "Third Strike",
      type: A,
      cluster: "lyt",
      effect:
        "Phantom Strike becomes a three-hit combo (was two). The third hit lands a heavier Decree — you didn't drop the string.",
    },
    {
      id: "st_l5a",
      name: "Bleed Specialist",
      type: F,
      cluster: "lyt",
      forkWith: "st_l5b",
      effect:
        "Phantom Strikes apply 1 stack of Bleed. Bleed deals 3 damage/sec, stacks up to 5.",
    },
    {
      id: "st_l5b",
      name: "Phase Specialist",
      type: F,
      cluster: "lyt",
      forkWith: "st_l5a",
      effect:
        "The iframe after the second swipe is doubled in duration. Bleed nodes lock out.",
    },
    {
      id: "st_l6",
      name: "Heart-Seeker",
      type: C,
      cluster: "lyt",
      requires: ["st_l5a"],
      effect:
        "Bleeding enemies take +50% damage from your Lyt attacks. Build-defining for the Bleed playstyle.",
    },
    {
      id: "st_l7",
      name: "Ghost-Walker",
      type: C,
      cluster: "lyt",
      requires: ["st_l5b"],
      effect:
        "iframes apply during the ENTIRE Phantom Strike combo, not just the second swipe. Build-defining for the Phase playstyle.",
    },

    // -------------- HAUNTING CLUSTER (4 slots) --------------
    {
      id: "st_h2",
      name: "Multi-Paw",
      type: P,
      cluster: "haunting",
      effect: "Spectral Paws now targets the TWO lowest-Anima enemies instead of one.",
    },
    {
      id: "st_h4",
      name: "Claws Trinity",
      type: A,
      cluster: "haunting",
      // Priced at the Impera's own threshold — a node that adds a
      // fourth claw should cost what holding the Claws costs.
      aenergyReq: { chao: 125 },
      effect: "Spectral Claws unleashes FOUR phantom claws (was three).",
    },
    {
      id: "st_h6",
      name: "Claws of Memory",
      type: P,
      cluster: "haunting",
      effect:
        "Enemies hit by Spectral Claws are MARKED. A marked target is an earned opening — every Phantom Strike into one Decrees.",
    },
    {
      id: "st_h9",
      name: "The Matriarch Remembers",
      type: C,
      cluster: "haunting",
      effect:
        "Haunting Presence's bleed now deals damage based on the enemy's MAX Anima, not flat. Turns the U'hlti into a boss-killer.",
    },

    // ------------- GHOST FORM CLUSTER (6 slots) -------------
    {
      id: "st_g1",
      name: "Ghost Form",
      type: A,
      cluster: "ghost_form",
      effect:
        "Unlocks the Transformation: Ghost Form. Slot 5 becomes usable. First node of the cluster.",
      restGated: true,
    },
    {
      id: "st_g2",
      name: "Extended Phase",
      type: P,
      cluster: "ghost_form",
      requires: ["st_g1"],
      effect: "Ghost Form duration increases from 8 sec to 12 sec.",
    },
    {
      id: "st_g4a",
      name: "Ghost's Anima",
      type: F,
      cluster: "ghost_form",
      requires: ["st_g1"],
      forkWith: "st_g4b",
      effect:
        "While in Ghost Form, you regenerate Anima equal to 5% of max per second.",
    },
    {
      id: "st_g4b",
      name: "Ghost's Strike",
      type: F,
      cluster: "ghost_form",
      requires: ["st_g1"],
      forkWith: "st_g4a",
      effect:
        "While in Ghost Form, your phantom swipes deal normal Lyt damage (was reduced). Anima regen locks out.",
    },
    {
      id: "st_g5",
      name: "Still Ghost",
      type: P,
      cluster: "ghost_form",
      requires: ["st_g4a"],
      effect:
        "Remain stationary in Ghost Form for 2 sec to become fully invisible — enemies lose targeting. Ambush tool.",
    },
    {
      id: "st_g6",
      name: "Holy's Bane",
      type: C,
      cluster: "ghost_form",
      requires: ["st_g4b"],
      effect:
        "Ghost Form is no longer vulnerable to Holy-affinity enemies. The Spectral Tiger's canonical weakness is overcome. 'I refuse to be what I am told I am.'",
    },

    // ---------------- GRACE CLUSTER (4 slots) ----------------
    {
      id: "st_k1",
      name: "Nine Lives",
      type: A,
      cluster: "grace",
      // ⚑ THE PRETTIEST GATE IN THE GAME. Doctrine Part 3: King's
      // Grace leans on Lyf and Lyt. The Tiger's transcribed Lyt
      // reads 6 / 6 / 8 across Beb / Champion / Lord — so a
      // threshold of 8 lands Nine Lives EXACTLY at the Ascent
      // without one line of code asking about tiers. And because
      // Lyt is circumventable, a Beb who finds a Lyt Tome can
      // take it early and live with what it cost them.
      aenergyReq: { lyt: 8 },
      effect: "Unlocks the King's Grace: Nine Lives. Slot 6 becomes usable.",
      restGated: true,
    },
    {
      id: "st_k4",
      name: "Twice Remembered",
      type: P,
      cluster: "grace",
      requires: ["st_k1"],
      effect:
        "Nine Lives can proc twice per village visit instead of once. Changes the rest-gated economy.",
    },
    {
      id: "st_k5",
      name: "Retributive Paws",
      type: P,
      cluster: "grace",
      requires: ["st_k1"],
      effect:
        "On Nine Lives trigger, all enemies within 20 meters take damage equal to 20% of their current Anima. Panic-button AoE.",
    },
    {
      id: "st_k6",
      name: "The Ninth",
      type: C,
      cluster: "grace",
      requires: ["st_k4"],
      effect:
        "The THIRD Nine Lives proc does not return you to life; it enters a 10-second 'ninth state' — invincible, double damage, automatic death at the end. The Spectral Tiger's final statement.",
    },

    // -------------- EMPERIUM CLUSTER (7 slots) --------------
    {
      id: "st_e1",
      name: "The Ghost Realm",
      type: A,
      cluster: "emperium",
      // Doctrine Part 3: an Emperium leans on ALL FOUR. This is
      // the hardest thing in the tree and it should read that way.
      // NOTE the Lyf: the Tiger's card goes 2 → 3 → 2, LOSING a
      // Lyf at Lord — so this must be 2, not 3, or the Emperium
      // would be unreachable at the exact tier it belongs to.
      // (The cards will keep doing this. Always check the Lord
      // row, never assume the stat climbs.)
      aenergyReq: { anima: 400, chao: 245, lyf: 2, lyt: 8 },
      // ⚑ THE CUT (Power Doctrine): the Haunting cluster went
      // from NINE nodes to FOUR, so a 5-point gate on it became
      // literally unreachable — the Emperium would have been
      // walled off forever. Retuned to 3, which is the same
      // PROPORTION of the cluster it always asked for (5/9 → 3/4
      // is close enough, and erring generous is correct here).
      // ⚑ Any future cut to `haunting` must re-check this number.
      clusterPointsReq: { cluster: "haunting", points: 3 },
      effect: "Unlocks the Emperium: The Ghost Realm. Slot 7 becomes usable.",
      restGated: true,
    },
    {
      id: "st_e2",
      name: "Vast Realm",
      type: P,
      cluster: "emperium",
      requires: ["st_e1"],
      effect: "The Ghost Realm's radius increases from 25 meters to 40 meters.",
    },
    {
      id: "st_e4",
      name: "Drag-In",
      type: P,
      cluster: "emperium",
      requires: ["st_e1"],
      effect:
        "Enemies outside the radius at Emperium activation are PULLED into it. No escape.",
    },
    {
      id: "st_e5",
      name: "Memory Chain",
      type: P,
      cluster: "emperium",
      requires: ["st_e1"],
      effect:
        "The monochrome effect persists 3 seconds after the Emperium ends — enemies caught in it deal -50% damage.",
    },
    {
      id: "st_e6a",
      name: "Realm of Claws",
      type: F,
      cluster: "emperium",
      requires: ["st_e1"],
      forkWith: "st_e6b",
      effect:
        "In the Ghost Realm, all your Lyt attacks auto-connect on all enemies simultaneously. Pure damage focus.",
    },
    {
      id: "st_e6b",
      name: "Realm of Dread",
      type: F,
      cluster: "emperium",
      requires: ["st_e1"],
      forkWith: "st_e6a",
      effect:
        "In the Ghost Realm, enemies flee in panic — cannot attack, can only run. CC over damage.",
    },
    {
      id: "st_e7",
      name: "The Final Realm",
      type: C,
      cluster: "emperium",
      requires: ["st_e1"],
      effect:
        "The Ghost Realm can be activated WITHOUT being rest-gated once per 24 real-world hours. The apex capstone — endgame Spectral Tigers use Emperium daily.",
    },
  ],
};

export default SPECTRAL_TIGER_TREE;
